import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import type { SortingRule } from 'react-table'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { getSortOptions } from 'components/Table/utils'
import { MobileSortBy } from 'components/Table'

import {
  domainPendingJobsDataSelector,
  domainPendingJobsSelector,
  selectedNavDomainSelector,
} from 'features/domains/selectors'
import DomainPendingJobsTable from '../DomainPendingJobsTable/DomainPendingJobsTable'
import { PENDING_JOBS_HEADERS } from './consts'
import { jobsTablesMessages } from '../DomainJobsHomesTable/messages'
import type { DomainType } from 'features/domains/types'
import { DomainJobsListWrapper } from 'features/domains/styles'
import { fetchDomainPendingJobsThunk } from 'features/domains/slice'
import { SpinnerCentered } from 'components/Loader'
import { makePublicUrl } from 'app/utils/publicUrl'
import NotFoundBlock from 'components/NotFoundBlock'

interface DomainPendingJobsProps {
  searchValue?: string
}

function DomainPendingJobs(props: DomainPendingJobsProps) {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()

  const domainPendingJobs = useAppSelector(domainPendingJobsSelector)
  const domainPendingJobsLoading = useAppSelector(
    domainPendingJobsDataSelector.isFetching
  )
  const domainPendingJobsLoaded = useAppSelector(
    domainPendingJobsDataSelector.isFinished
  )
  const domain = useAppSelector(selectedNavDomainSelector.data)
  const domainId: string | undefined = domain?.id
  const domainIsLoading = useAppSelector(selectedNavDomainSelector.isFetching)

  const isLoading =
    !domainPendingJobsLoaded && (domainPendingJobsLoading || domainIsLoading)

  const fetchDomainPendingJobs = useCallback(() => {
    if (domainId) {
      dispatch(fetchDomainPendingJobsThunk(domainId))
    }
  }, [dispatch, domainId])

  useEffect(() => {
    if (fetchDomainPendingJobs) {
      fetchDomainPendingJobs()
    }
  }, [fetchDomainPendingJobs])

  const headers = useMemo(
    () => ({
      name: {
        label: formatMessage(jobsTablesMessages.jobName),
        id: PENDING_JOBS_HEADERS.NAME,
      },
      updated: {
        label: formatMessage(jobsTablesMessages.updated),
        id: PENDING_JOBS_HEADERS.UPDATED,
      },
    }),
    [formatMessage]
  )

  const sortOptions = useMemo(() => getSortOptions(headers), [headers])
  const [sortBy, setSortBy] = useState<SortingRule<DomainType>>(sortOptions[0])

  if (isLoading) {
    return <SpinnerCentered />
  }

  return (
    <DomainJobsListWrapper>
      {domainPendingJobs?.length ? (
        <>
          <MobileSortBy<DomainType>
            headers={headers}
            sortOptions={sortOptions}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
          <DomainPendingJobsTable
            data={domainPendingJobs}
            headers={headers}
            defaultSortBy={sortOptions[0]}
            sortBy={sortBy}
            setSortBy={setSortBy}
            searchValue={props.searchValue}
          />
        </>
      ) : (
        <NotFoundBlock
          imgSrc={makePublicUrl('images/illustrations/homes-illustration.svg')}
          title={'No pending jobs found'}
          text={''}
        />
      )}
    </DomainJobsListWrapper>
  )
}

export default DomainPendingJobs
